"use client";

import * as React from "react";
import { format, startOfDay } from "date-fns";
import { useTranslation } from "react-i18next";
import type { DateRange as RDPDateRange } from "react-day-picker";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/primitives/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/overlays/popover";
import { Calendar } from "@/components/ui/calendar";
import { DATE_FORMATS } from "@/lib/date/config";
import {
  parseDateFromInput,
  formatDateInputAsYouType,
} from "@/lib/date";
import { toast } from "sonner";
import { NumberRulesInput } from "@/components/ui/forms/number-rules-input";
import type { NumberRule } from "@/components/ui/forms/number-rules-input";

// =============================================================================
// Types
// =============================================================================

export type SearchFilterType = "text" | "daterange" | "date" | "numberRules" | "multiSelect" | "tags";

export type { NumberRule };

export interface MultiSelectOption {
  value: string;
  label: string;
}

export interface SearchableColumn {
  key: string;
  label: string;
  placeholder?: string;
  filterType: SearchFilterType;
  /**
   * When `filterType` is `"text"`, only allow decimal digits (e.g. int64 reference fields).
   */
  integerOnly?: boolean;
  /** Required when filterType is "multiSelect" */
  options?: MultiSelectOption[];
  /** When "lowercase", API value is comma-joined lowercased (e.g. eq-Mutation) */
  multiSelectApiFormat?: "lowercase" | "default";
}

export interface DateRange {
  from: Date | undefined;
  to: Date | undefined;
}

export interface ColumnSearchInputProps {
  columns: SearchableColumn[];
  selectedColumn: string;
  onColumnChange: (columnKey: string) => void;
  textValue: string;
  onTextChange: (value: string) => void;
  dateRangeValue?: DateRange;
  onDateRangeChange?: (range: DateRange) => void;
  singleDateValue?: Date;
  onSingleDateChange?: (date: Date | undefined) => void;
  numberRulesValue?: NumberRule[];
  onNumberRulesChange?: (rules: NumberRule[]) => void;
  multiSelectValue?: string[];
  onMultiSelectChange?: (value: string[]) => void;
  tagsValue?: string[];
  onTagsChange?: (value: string[]) => void;
  onSearch: () => void;
  className?: string;
}

// =============================================================================
// Column Search Input
// =============================================================================

function ColumnSearchInput({
  columns,
  selectedColumn,
  onColumnChange,
  textValue,
  onTextChange,
  dateRangeValue,
  onDateRangeChange,
  singleDateValue,
  onSingleDateChange,
  numberRulesValue = [],
  onNumberRulesChange,
  multiSelectValue = [],
  onMultiSelectChange,
  onSearch,
  className,
}: ColumnSearchInputProps) {
  const activeColumn = columns.find((c) => c.key === selectedColumn);
  const filterType = activeColumn?.filterType ?? "text";
  const dateRangeInputRef = React.useRef<DateRangeInputRef>(null);
  const singleDateInputRef = React.useRef<SingleDateInputRef>(null);

  const handleSearch = () => {
    if (filterType === "daterange") {
      const ok = dateRangeInputRef.current?.validateAndCommit?.() ?? true;
      if (!ok) return;
    }
    if (filterType === "date") {
      const ok = singleDateInputRef.current?.validateAndCommit?.() ?? true;
      if (!ok) return;
    }
    if (filterType === "multiSelect" && multiSelectValue.length === 0) return;
    if (filterType === "tags" && !textValue.trim()) return;
    onSearch();
  };

  const searchDisabled =
    (filterType === "multiSelect" && multiSelectValue.length === 0) ||
    (filterType === "tags" && !textValue.trim());

  return (
    <div className={cn(
        "group/searchbar flex items-stretch h-[38px]",
        "rounded-sm outline-1 -outline-offset-1 outline-transparent",
        "focus-within:outline-[1px] focus-within:-outline-offset-[1px] focus-within:outline-(--input-border-focus)",
        "transition-colors",
        className,
      )}>
        {/* Column selector dropdown */}
        <ColumnSelector
        columns={columns}
        value={selectedColumn}
        onChange={onColumnChange}
        triggerClassName="group-focus-within/searchbar:border-(--input-border-focus)"
      />

      {/* Dynamic input area based on filter type */}
      {filterType === "text" || filterType === "tags" ? (
        <TextSearchInput
          value={textValue}
          onChange={onTextChange}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          placeholder={activeColumn?.placeholder ?? `Search ${activeColumn?.label ?? ""}`}
          integerOnly={!!activeColumn?.integerOnly}
        />
      ) : filterType === "date" ? (
        <SingleDateInput
          ref={singleDateInputRef}
          columnKey={selectedColumn}
          date={singleDateValue}
          onSelect={onSingleDateChange ?? (() => {})}
          placeholder={activeColumn?.placeholder ?? `Select ${activeColumn?.label ?? ""}`}
          className="flex-1"
        />
      ) : filterType === "numberRules" ? (
        <NumberRulesInput
          columnLabel={activeColumn?.label ?? ""}
          columnKey={activeColumn?.key}
          rules={numberRulesValue}
          onRulesChange={onNumberRulesChange ?? (() => {})}
          onApply={onSearch}
        />
      ) : filterType === "multiSelect" ? (
        <MultiSelectSearchInput
          options={activeColumn?.options ?? []}
          value={multiSelectValue}
          onChange={onMultiSelectChange ?? (() => {})}
          onApply={onSearch}
          placeholder={activeColumn?.placeholder ?? `Search ${activeColumn?.label ?? ""}`}
        />
      ) : (
        <DateRangeTwoColumnInput
          ref={dateRangeInputRef}
          value={dateRangeValue}
          onChange={onDateRangeChange ?? (() => {})}
        />
      )}

        {/* Search icon addon */}
        <button
          type="button"
          onClick={handleSearch}
          disabled={searchDisabled}
          className={cn(
            "flex items-center justify-center shrink-0 w-[35px] border border-border-secondary bg-bg-primary rounded-r-sm -ml-px transition-colors group-focus-within/searchbar:border-(--input-border-focus)",
            searchDisabled
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-bg-hover"
          )}
          aria-label="Search"
        >
          <i className="pi pi-search text-sm text-text-tertiary" />
        </button>
      </div>
  );
}

// =============================================================================
// MultiSelect Search Input (trigger with chevron, popover with checkboxes)
// =============================================================================

function MultiSelectSearchInput({
  options,
  value,
  onChange,
  onApply,
  placeholder,
}: {
  options: MultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  onApply: () => void;
  placeholder: string;
}) {
  const [open, setOpen] = React.useState(false);

  const toggleOption = (optValue: string) => {
    if (value.includes(optValue)) {
      onChange(value.filter((v) => v !== optValue));
    } else {
      onChange([...value, optValue]);
    }
  };

  const displayText =
    value.length > 0
      ? value
          .map((v) => options.find((o) => o.value === v)?.label ?? v)
          .join(", ")
      : "";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex items-center gap-2 flex-1 min-w-0 -ml-px",
            "border border-border-secondary",
            "rounded-none",
            "px-[11.5px] py-[8px]",
            "bg-bg-primary text-left",
            "shadow-[0px_1px_2px_0px_rgba(18,18,23,0.05)]",
            "outline-none",
            "hover:bg-bg-hover transition-colors"
          )}
        >
          <span
            className={cn(
              "body-sm-regular truncate flex-1 min-w-0",
              displayText ? "text-text-primary" : "text-text-tertiary"
            )}
          >
            {displayText || placeholder}
          </span>
          <i className="pi pi-chevron-down text-xs text-text-tertiary shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-0 bg-white rounded-b-md" align="end">
        <div className="p-2 max-h-[280px] overflow-y-auto">
          {options.length === 0 ? (
            <div className="py-2 text-sm text-text-tertiary">No options</div>
          ) : (
            options.map((opt) => (
              <label
                key={opt.value}
                className={cn(
                  "flex items-center gap-2 py-1.5 px-2 rounded-sm cursor-pointer",
                  "hover:bg-bg-hover transition-colors"
                )}
              >
                <input
                  type="checkbox"
                  checked={value.includes(opt.value)}
                  onChange={() => toggleOption(opt.value)}
                  className="rounded border-border-secondary accent-brand-600"
                />
                <span className="text-sm text-text-primary">{opt.label}</span>
              </label>
            ))
          )}
        </div>
        <div className="flex gap-2 p-2">
          <button
            type="button"
            onClick={() => onChange([])}
            className="flex-1 px-3 py-2 text-sm font-medium rounded-sm border border-border-secondary text-text-secondary hover:bg-bg-hover transition-colors"
          >
            Clear
          </button>
          <button
            type="button"
            disabled={value.length === 0}
            onClick={() => {
              if (value.length === 0) return;
              onApply();
              setOpen(false);
            }}
            className={cn(
              "flex-1 px-3 py-2 text-sm font-medium rounded-sm bg-brand-600 text-white transition-colors",
              "hover:bg-brand-700",
              "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-brand-600"
            )}
          >
            Apply
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// =============================================================================
// Column Selector (left dropdown)
// =============================================================================

function ColumnSelector({
  columns,
  value,
  onChange,
  triggerClassName,
}: {
  columns: SearchableColumn[];
  value: string;
  onChange: (key: string) => void;
  /** Applied to the trigger button (e.g. for group-focus-within border) */
  triggerClassName?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  const selectedLabel = columns.find((c) => c.key === value)?.label ?? "";

  const filtered = search
    ? columns.filter((c) => c.label.toLowerCase().includes(search.toLowerCase()))
    : columns;

  const handleSelect = (key: string) => {
    onChange(key);
    setOpen(false);
    setSearch("");
  };

  return (
    <Popover open={open} onOpenChange={(next) => {
      setOpen(next);
      if (!next) setSearch("");
    }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex items-center gap-2 shrink-0",
            "border border-border-secondary border-r-0 bg-bg-primary",
            "rounded-l-sm rounded-r-none",
            "pl-[10.5px] pr-0 py-[7px]",
            "text-sm text-text-primary",
            "shadow-[0px_1px_2px_0px_rgba(18,18,23,0.05)]",
            "outline-none",
            "hover:bg-bg-hover transition-colors",
            triggerClassName
          )}
        >
          <i className="pi pi-filter text-sm text-text-tertiary shrink-0" />
          <span
            className="max-w-[10.5rem] truncate"
            title={selectedLabel}
          >
            {selectedLabel}
          </span>
          <div className="flex items-center justify-center w-[35px] shrink-0">
            <i className="pi pi-chevron-down text-xs text-text-tertiary" />
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-56 p-0 bg-white"
        align="start"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          searchInputRef.current?.focus();
        }}
      >
        <div className="p-2 border-b border-border-primary border-b-0">
          <input
            ref={searchInputRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search columns..."
            className={cn(
              "w-full px-2 py-1.5 text-sm rounded-sm",
              "bg-bg-primary text-text-primary",
              "border border-border-secondary",
              "placeholder:text-text-tertiary",
              "outline-none focus:border-(--input-border-focus)",
              "transition-colors"
            )}
          />
        </div>
        <div className="p-1 max-h-[300px] overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="py-4 text-center text-sm text-text-tertiary">
              No columns found
            </div>
          ) : (
            filtered.map((col) => (
              <button
                key={col.key}
                type="button"
                onClick={() => handleSelect(col.key)}
                className={cn(
                  "relative flex w-full cursor-default items-center gap-2 rounded-sm",
                  "py-1.5 pr-8 pl-2 text-sm select-none",
                  "hover:bg-bg-hover transition-colors text-left"
                )}
              >
                <span>{col.label}</span>
                {col.key === value && (
                  <span className="absolute right-2 flex size-3.5 items-center justify-center">
                    <i className="pi pi-check text-sm" />
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// =============================================================================
// Text Search Input (middle section for text filter type)
// =============================================================================

function TextSearchInput({
  value,
  onChange,
  onKeyDown,
  placeholder,
  integerOnly = false,
}: {
  value: string;
  onChange: (value: string) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  placeholder: string;
  integerOnly?: boolean;
}) {
  const handleChange = (raw: string) => {
    onChange(integerOnly ? raw.replace(/\D/g, "") : raw);
  };

  return (
    <div className="flex-1 min-w-0 -ml-px">
      <input
        type="text"
        inputMode={integerOnly ? "numeric" : undefined}
        autoComplete="off"
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className={cn(
          "w-full h-full",
          "border border-border-secondary",
          "rounded-none",
          "px-[11.5px] py-[8px]",
          "bg-bg-primary text-text-primary body-sm-regular",
          "shadow-[0px_1px_2px_0px_rgba(18,18,23,0.05)]",
          "placeholder:text-text-tertiary",
          "outline-none",
          "transition-colors"
        )}
      />
    </div>
  );
}

// =============================================================================
// Date Range – two-column "From" | "To" trigger opening one range picker
// =============================================================================

export interface DateRangeInputRef {
  validateAndCommit: () => boolean;
}

export interface SingleDateInputRef {
  validateAndCommit: () => boolean;
}

const DateRangeTwoColumnInput = React.forwardRef<
  DateRangeInputRef,
  {
    value?: DateRange;
    onChange?: (range: DateRange) => void;
  }
>(function DateRangeTwoColumnInput({ value, onChange }, ref) {
  const { t } = useTranslation("common");
  const [isOpen, setIsOpen] = React.useState(false);
  const [fromFocused, setFromFocused] = React.useState(false);
  const [toFocused, setToFocused] = React.useState(false);
  const [fromInputText, setFromInputText] = React.useState("");
  const [toInputText, setToInputText] = React.useState("");
  const [fromError, setFromError] = React.useState<string | null>(null);
  const [toError, setToError] = React.useState<string | null>(null);
  /** When true, keep showing invalid From text after blur (do not revert to value.from) */
  const [fromShowInvalidText, setFromShowInvalidText] = React.useState(false);
  /** When true, keep showing invalid To text after blur (do not revert to value.to) */
  const [toShowInvalidText, setToShowInvalidText] = React.useState(false);

  const selected = value?.from || value?.to
    ? { from: value?.from, to: value?.to }
    : undefined;

  const handleSelect = (range: RDPDateRange | undefined, selectedDay?: Date) => {
    if (!range?.from) {
      onChange?.({ from: undefined, to: undefined });
      return;
    }
    const currentHasRange = value?.from != null && value?.to != null;
    // When we already have a full range and user clicks again = re-selecting From. Clear To.
    // v9 may extend range (e.g. from=oldTo, to=clicked) instead of resetting; use selectedDay or range.to as the clicked day.
    if (currentHasRange) {
      const clicked = startOfDay(selectedDay ?? range.to ?? range.from);
      onChange?.({ from: clicked, to: undefined });
      return;
    }
    const fromOnly =
      !range.to ||
      startOfDay(range.from).getTime() === startOfDay(range.to).getTime();
    if (fromOnly) {
      onChange?.({ from: range.from, to: undefined });
      return;
    }
    onChange?.({ from: range.from, to: range.to });
    setIsOpen(false);
  };

  const handleToday = () => {
    const today = startOfDay(new Date());
    onChange?.({ from: today, to: today });
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange?.({ from: undefined, to: undefined });
  };

  const commitFrom = (showError: boolean) => {
    const trimmed = fromInputText.trim();
    if (!trimmed) {
      onChange?.({ from: undefined, to: value?.to });
      setFromInputText("");
      setFromError(null);
      setFromShowInvalidText(false);
      return true;
    }
    const parsed = parseDateFromInput(fromInputText);
    if (parsed) {
      onChange?.({ from: parsed, to: value?.to });
      setFromInputText("");
      setFromError(null);
      setFromShowInvalidText(false);
      return true;
    }
    if (showError) setFromError(t("invalidFromDate"));
    else setFromError(null);
    setFromShowInvalidText(true);
    return false;
  };
  const commitTo = (showError: boolean) => {
    const trimmed = toInputText.trim();
    if (!trimmed) {
      onChange?.({ from: value?.from, to: undefined });
      setToInputText("");
      setToError(null);
      setToShowInvalidText(false);
      return true;
    }
    const parsed = parseDateFromInput(toInputText);
    if (parsed) {
      onChange?.({ from: value?.from, to: parsed });
      setToInputText("");
      setToError(null);
      setToShowInvalidText(false);
      return true;
    }
    if (showError) setToError(t("invalidToDate"));
    else setToError(null);
    setToShowInvalidText(true);
    return false;
  };

  const fromDisplay = fromFocused
    ? fromInputText
    : fromShowInvalidText
      ? fromInputText
      : value?.from
        ? format(value.from, DATE_FORMATS.short)
        : "";
  const toDisplay = toFocused
    ? toInputText
    : toShowInvalidText
      ? toInputText
      : value?.to
        ? format(value.to, DATE_FORMATS.short)
        : "";

  const segmentClass = cn(
    // `min-w-[8.5rem]` fits `dd-MM-yyyy` (10 chars) + calendar icon; `shrink-0`
    // stops flex from clipping the year when the column label is long.
    "flex items-center gap-[12.25px] flex-1 min-w-[8.5rem] shrink-0 px-[11.5px] py-[8px]",
    "bg-bg-primary text-left",
    "border border-border-secondary rounded-none",
    "shadow-[0px_1px_2px_0px_rgba(18,18,23,0.05)]",
    "outline-none transition-colors",
    "group-focus-within/searchbar:border-(--input-border-focus)"
  );
  const inputClass = cn(
    "min-w-[5.5rem] flex-1 bg-transparent border-none p-0 body-sm-regular outline-none tabular-nums",
    "placeholder:text-text-tertiary text-text-primary"
  );

  React.useImperativeHandle(ref, () => ({
    validateAndCommit() {
      setFromError(null);
      setToError(null);
      const fromTrimmed = fromInputText.trim();
      const toTrimmed = toInputText.trim();
      const fromParsed = fromTrimmed ? parseDateFromInput(fromInputText) : null;
      const toParsed = toTrimmed ? parseDateFromInput(toInputText) : null;
      const fromValid = !fromTrimmed || !!fromParsed;
      const toValid = !toTrimmed || !!toParsed;
      if (fromValid && toValid) {
        onChange?.({
          from: fromParsed ?? undefined,
          to: toParsed ?? undefined,
        });
        setFromInputText("");
        setToInputText("");
        setFromShowInvalidText(false);
        setToShowInvalidText(false);
        return true;
      }
      if (!fromValid) setFromError(t("invalidFromDate"));
      if (!toValid) setToError(t("invalidToDate"));
      const toastMessage =
        !fromValid && !toValid
          ? t("invalidFromAndToDate")
          : !fromValid
            ? t("invalidFromDate")
            : t("invalidToDate");
      toast.error(toastMessage, { position: "top-right" });
      return false;
    },
  }));

  return (
    <div className="relative flex flex-1 min-w-0 -ml-px h-[38px] overflow-x-auto">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <span
          className={cn(
            segmentClass,
            "-mr-px",
            fromError && "relative z-10 !border !border-error-500"
          )}
        >
          <PopoverTrigger asChild>
            <button
              type="button"
              className="shrink-0 p-0 bg-transparent border-none cursor-pointer text-text-tertiary hover:text-text-secondary outline-none"
              aria-label="Open calendar"
            >
              <i className="pi pi-calendar text-sm" />
            </button>
          </PopoverTrigger>
          <input
            type="text"
            placeholder="From"
            value={fromDisplay}
            onChange={(e) =>
              setFromInputText(
                formatDateInputAsYouType(e.target.value, DATE_FORMATS.short)
              )
            }
            onFocus={() => {
              setFromFocused(true);
              setFromError(null);
              setFromShowInvalidText(false);
              setFromInputText(
                fromShowInvalidText
                  ? fromInputText
                  : value?.from
                    ? format(value.from, DATE_FORMATS.short)
                    : ""
              );
            }}
            onBlur={() => {
              setFromFocused(false);
              commitFrom(false);
            }}
            onKeyDown={(e) =>
              e.key === "Enter" && (e.target as HTMLInputElement).blur()
            }
            className={inputClass}
          />
        </span>
        <PopoverContent className="w-auto p-0 bg-white rounded-lg overflow-hidden" align="start">
          <Calendar
            mode="range"
            defaultMonth={value?.from}
            selected={selected}
            onSelect={handleSelect}
            numberOfMonths={2}
            captionLayout="dropdown"
            startMonth={new Date(2020, 0)}
            endMonth={new Date(2040, 11)}
            className="rounded-none border-0 shadow-none"
            required={false}
          />
          <div className="flex gap-2 p-2 border-t border-border-secondary bg-white">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={handleClear}
            >
              {t("clear")}
            </Button>
            <Button
              type="button"
              variant="default"
              size="sm"
              className="flex-1"
              onClick={handleToday}
            >
              {t("today")}
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      <span
        className={cn(
          segmentClass,
          toError && "relative z-10 !border !border-error-500"
        )}
      >
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="shrink-0 p-0 bg-transparent border-none cursor-pointer text-text-tertiary hover:text-text-secondary outline-none"
          aria-label="Open calendar"
        >
          <i className="pi pi-calendar text-sm" />
        </button>
        <input
          type="text"
          placeholder="To"
          value={toDisplay}
          onChange={(e) =>
            setToInputText(
              formatDateInputAsYouType(e.target.value, DATE_FORMATS.short)
            )
          }
          onFocus={() => {
            setToFocused(true);
            setToError(null);
            setToShowInvalidText(false);
            setToInputText(
              toShowInvalidText
                ? toInputText
                : value?.to
                  ? format(value.to, DATE_FORMATS.short)
                  : ""
            );
          }}
          onBlur={() => {
            setToFocused(false);
            commitTo(false);
          }}
          onKeyDown={(e) =>
            e.key === "Enter" && (e.target as HTMLInputElement).blur()
          }
          className={inputClass}
        />
      </span>
    </div>
  );
});

// =============================================================================
// Single Date Input (used for filterType === "date") – typeable, validate on search
// =============================================================================

const SingleDateInput = React.forwardRef<
  SingleDateInputRef,
  {
    columnKey: string;
    date?: Date;
    onSelect: (date: Date | undefined) => void;
    placeholder: string;
    className?: string;
  }
>(function SingleDateInput({ columnKey, date, onSelect, placeholder, className }, ref) {
  const { t } = useTranslation("common");
  const [isOpen, setIsOpen] = React.useState(false);
  const [inputText, setInputText] = React.useState("");
  const [showInvalidText, setShowInvalidText] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // When user switches column (e.g. Expiry date → Production date), clear input so the new column starts empty
  React.useLayoutEffect(() => {
    setInputText("");
    setShowInvalidText(false);
    setError(null);
  }, [columnKey]);

  const displayValue =
    showInvalidText
      ? inputText
      : inputText !== ""
        ? inputText
        : date
          ? format(date, DATE_FORMATS.short)
          : "";

  const validateAndCommit = (): boolean => {
    const trimmed = inputText.trim();
    if (!trimmed) {
      onSelect(undefined);
      setInputText("");
      setError(null);
      setShowInvalidText(false);
      return true;
    }
    const parsed = parseDateFromInput(inputText);
    if (parsed) {
      onSelect(parsed);
      setInputText("");
      setError(null);
      setShowInvalidText(false);
      return true;
    }
    const message = t("invalidDate");
    setError(message);
    setShowInvalidText(true);
    toast.error(message, { position: "top-right" });
    return false;
  };

  React.useImperativeHandle(ref, () => ({
    validateAndCommit,
  }));

  const handleSelect = (selected: Date | undefined) => {
    onSelect(selected);
    setInputText("");
    setError(null);
    setShowInvalidText(false);
    setIsOpen(false);
  };

  const handleToday = () => {
    onSelect(startOfDay(new Date()));
    setInputText("");
    setError(null);
    setShowInvalidText(false);
    setIsOpen(false);
  };

  const handleClear = () => {
    onSelect(undefined);
    setInputText("");
    setError(null);
    setShowInvalidText(false);
  };

  const segmentClass = cn(
    "flex items-center gap-[12.25px] flex-1 min-w-0 -ml-px px-[11.5px] py-[8px]",
    "bg-bg-primary text-left",
    "border border-border-secondary rounded-none",
    "shadow-[0px_1px_2px_0px_rgba(18,18,23,0.05)]",
    "outline-none transition-colors",
    "group-focus-within/searchbar:border-(--input-border-focus)"
  );
  const inputClass = cn(
    "flex-1 min-w-0 bg-transparent border-none p-0 body-sm-regular outline-none",
    "placeholder:text-text-tertiary text-text-primary"
  );

  return (
    <div className="relative flex flex-1 min-w-0 h-[38px]">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <span
          className={cn(
            segmentClass,
            className,
            error && "relative z-10 !border !border-error-500"
          )}
        >
          <PopoverTrigger asChild>
            <button
              type="button"
              className="shrink-0 p-0 bg-transparent border-none cursor-pointer text-text-tertiary hover:text-text-secondary outline-none"
              aria-label="Open calendar"
            >
              <i className="pi pi-calendar text-sm" />
            </button>
          </PopoverTrigger>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            placeholder={placeholder}
            value={displayValue}
            onChange={(e) => {
              setInputText(
                formatDateInputAsYouType(e.target.value, DATE_FORMATS.short)
              );
              setError(null);
              setShowInvalidText(false);
            }}
            onFocus={() => {
              if (!showInvalidText)
                setInputText(
                  date ? format(date, DATE_FORMATS.short) : ""
                );
            }}
            onBlur={() => {
              if (showInvalidText) return;
              const trimmed = inputText.trim();
              if (!trimmed) {
                onSelect(undefined);
                setInputText("");
                return;
              }
              const parsed = parseDateFromInput(inputText);
              if (parsed) {
                onSelect(parsed);
                setInputText("");
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            }}
            className={inputClass}
          />
        </span>
        <PopoverContent className="w-auto p-0 bg-white rounded-lg overflow-hidden" align="start">
          <Calendar
            mode="single"
            selected={date}
            onSelect={handleSelect}
            captionLayout="dropdown"
            startMonth={new Date(2020, 0)}
            endMonth={new Date(2040, 11)}
            className="rounded-none border-0 shadow-none"
          />
          <div className="flex gap-2 p-2 border-t border-border-secondary bg-white">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={handleClear}
            >
              {t("clear")}
            </Button>
            <Button
              type="button"
              variant="default"
              size="sm"
              className="flex-1"
              onClick={handleToday}
            >
              {t("today")}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
});

ColumnSearchInput.displayName = "ColumnSearchInput";

export { ColumnSearchInput };
export type { DateRange as ColumnSearchDateRange };
